"use client";

import React from "react";
import { ArrowRight, Building2, Hammer, UtensilsCrossed, UserCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const industryFocus = [{
  title: "Local trades & contractors",
  description: "Targeted regional visibility to capture high-intent inquiries across domestic and commercial projects.",
  icon: Hammer
}, {
  title: "Hospitality & venues",
  description: "Local discovery, map pack presence, and seasonal organic reach for restaurants, hotels, and event spaces.",
  icon: UtensilsCrossed
}, {
  title: "Property & real estate",
  description: "Structured architecture and authoritative positioning for agencies, developers, and commercial brokers.",
  icon: Building2
}];
export default function HubHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="industryhubhero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28">
      {/* Decorative ambient background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-1/2 h-96 w-96 translate-x-1/2 rounded-full bg-secondary/35 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-20 right-10 h-72 w-72 rounded-full bg-accent/10 blur-2xl" />

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="mx-auto flex max-w-3xl flex-col items-center text-center">
          {/* Eyebrow badge */}
          <motion.div variants={itemVariants} className="mb-6">
            <Badge variant="outline" className="border-border bg-secondary/40 px-3.5 py-1 text-xs font-medium text-foreground">
              <UserCheck className="size-3.5 text-primary" />
              Direct Consultant Access
            </Badge>
          </motion.div>

          {/* Primary H1 */}
          <motion.h1 variants={itemVariants} className="font-default text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            SEO strategy for trades, hospitality, and property businesses
          </motion.h1>

          {/* Value proposition copy */}
          <motion.p variants={itemVariants} className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
            I work directly with you to build sustainable organic search visibility tailored to your sector.
          </motion.p>

          {/* Call to action buttons */}
          <motion.div variants={itemVariants} className="mt-8 flex w-full flex-col items-center justify-center gap-3 sm:w-auto sm:flex-row">
            <Button asChild size="lg" className="w-full sm:w-auto rounded-md bg-primary px-6 text-primary-foreground shadow-xs hover:bg-primary/90">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-md border-border bg-background px-6 text-foreground shadow-xs hover:bg-muted">
              <Link to="/CaseStudiesPage">Explore My Case Studies</Link>
            </Button>
          </motion.div>
        </motion.div>

        {/* Sector Pillars */}
        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {industryFocus.map((sector, i) => {
          const Icon = sector.icon;
          return <div key={sector.title} data-index={i} className="group relative rounded-xl border border-border bg-card p-6 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                <div className="flex size-11 items-center justify-center rounded-lg bg-secondary/60 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <Icon className="size-5" />
                </div>
                <h2 className="mt-4 font-default text-lg font-semibold text-card-foreground">
                  {sector.title}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {sector.description}
                </p>
              </div>;
        })}
        </div>
      </div>
    </section>;
}